import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Popover,
} from '@mui/material';
import {
  Check as AcceptIcon,
  Edit as EditIcon,
  Close as RejectIcon,
  Create as MarkIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { EntrySuggestion, Library } from '@/types/api';
import { EntryStatus } from '@/types/enums';
import { useCreateEntry, useCreateEntryMedia } from '@/hooks/useEntries';
import { MediaRole } from '@/types/enums';
import ConfidenceBadge from './ConfidenceBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SelectableTextField, { TextSelection } from '@/components/common/SelectableTextField';
import { learnFromSelection } from '@/api/patterns';
import CreateLibraryDialog from '@/components/indexChannel/CreateLibraryDialog';
import { useCreateLibrary } from '@/hooks/useLibraries';
import { useCreateLibraryAlias } from '@/hooks/useLibraryAliases';
import { LibraryAliasType } from '@/types/enums';

interface SuggestionReviewDialogProps {
  open: boolean;
  suggestion: EntrySuggestion | null;
  chatId: number;
  libraries: Library[];
  readOnly?: boolean;
  mode?: 'edit' | 'review';
  onRefreshLibraries?: () => Promise<unknown> | void;
  onViewEntry?: (entryId: number) => void;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
}

const CREATE_LIBRARY_OPTION = '__create_new_library__';

const SuggestionReviewDialog: React.FC<SuggestionReviewDialogProps> = ({
  open,
  suggestion,
  chatId,
  libraries,
  readOnly = false,
  mode = 'review',
  onRefreshLibraries,
  onViewEntry,
  onClose,
  onAccept,
  onReject,
}) => {
  const { t } = useTranslation('components');
  const createEntry = useCreateEntry();
  const createEntryMedia = useCreateEntryMedia();
  const createLibraryMutation = useCreateLibrary();
  const createAliasMutation = useCreateLibraryAlias();

  const [isEditing, setIsEditing] = useState(true);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [msNo, setMsNo] = useState('');
  const [libraryId, setLibraryId] = useState<number | ''>('');
  const [status, setStatus] = useState<EntryStatus>(EntryStatus.DRAFT);
  const [notes, setNotes] = useState('');

  // Pattern learning state
  const [titleSelection, setTitleSelection] = useState<TextSelection | null>(null);
  const [selectionAnchorEl, setSelectionAnchorEl] = useState<HTMLElement | null>(null);
  const [isLearningPattern, setIsLearningPattern] = useState(false);
  const [createLibraryOpen, setCreateLibraryOpen] = useState(false);

  const isReadOnly = readOnly || Boolean(suggestion?.is_indexed) || Boolean(suggestion?.is_missing_match);

  // Reset form when suggestion changes - start in edit mode immediately
  useEffect(() => {
    if (suggestion) {
      setTitle(suggestion.title || '');
      setAuthor(suggestion.author || '');
      setMsNo(suggestion.ms_no || '');
      setLibraryId(
        suggestion.library_matches.length > 0 ? suggestion.library_matches[0].library_id : ''
      );
      setStatus(EntryStatus.DRAFT);
      setNotes('');
      setIsEditing(mode === 'edit' && !isReadOnly);
    }
  }, [suggestion, isReadOnly, mode]);

  // Helper function to translate pattern elements
  const translatePattern = (pattern: string): string => {
    // Pattern format: "ELEMENT1_ELEMENT2_ELEMENT3" e.g., "separator_title_media"
    // Convert to uppercase for translation lookup
    const parts = pattern.toUpperCase().split('_');
    const translatedParts = parts.map((part) => {
      // Try to translate individual element type, fallback to uppercase
      return t(`customPatternBuilder.patternElementTypes.${part}`, part);
    });
    return translatedParts.join(' → ');
  };

  if (!suggestion) return null;

  const handleLibraryChange = (value: string | number) => {
    if (value === CREATE_LIBRARY_OPTION) {
      setCreateLibraryOpen(true);
      return;
    }
    if (value === '') {
      setLibraryId('');
      return;
    }
    setLibraryId(Number(value));
  };

  const handleCreateLibrarySubmit = async (data: {
    canonical_name: string;
    slug: string;
    notes: string;
    aliases: string[];
  }) => {
    try {
      const newLibrary = await createLibraryMutation.mutateAsync({
        canonical_name: data.canonical_name,
        slug: data.slug || data.canonical_name.toLowerCase().replace(/\s+/g, '-'),
        notes: data.notes || undefined,
      });

      if (data.aliases.length > 0) {
        for (const aliasText of data.aliases) {
          try {
            await createAliasMutation.mutateAsync({
              libraryId: newLibrary.id,
              data: {
                alias: aliasText,
                alias_type: aliasText.startsWith('#')
                  ? LibraryAliasType.HASHTAG
                  : LibraryAliasType.TEXT,
              },
            });
          } catch (error) {
            console.error(`Failed to create alias "${aliasText}":`, error);
          }
        }
      }

      await onRefreshLibraries?.();
      setLibraryId(newLibrary.id);
      setCreateLibraryOpen(false);
    } catch (error) {
      console.error('Failed to create library:', error);
      alert(
        t(
          'suggestionReviewDialog.createLibrary.failed',
          'Failed to create library. Please try again.'
        )
      );
    }
  };

  const handleAccept = async () => {
    if (isReadOnly) {
      return;
    }
    if (!libraryId) {
      alert(t('suggestionReviewDialog.form.noLibraryMatch'));
      return;
    }

    try {
      // Create entry
      const entry = await createEntry.mutateAsync({
        chat_id: chatId,
        library_id: libraryId,
        title,
        author: author || undefined,
        ms_no: msNo || undefined,
        title_message_id: suggestion.title_message_id,
        status,
        notes: notes || undefined,
      });

      // Link media files
      for (let i = 0; i < suggestion.media_ids.length; i++) {
        await createEntryMedia.mutateAsync({
          entryId: entry.id,
          data: {
            media_id: suggestion.media_ids[i],
            source_message_id: suggestion.media_message_ids[i],
            source_message_row_id: suggestion.title_message_id,
            role: i === 0 ? MediaRole.PRIMARY : MediaRole.SUPPLEMENTARY,
            order_index: i,
          },
        });
      }

      onAccept();
    } catch (error) {
      console.error('Failed to create entry:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleReject = () => {
    if (isReadOnly) {
      return;
    }
    onReject();
  };

  // Handler for text selection in title field
  const handleTitleSelect = (selection: TextSelection, event: React.MouseEvent<HTMLInputElement>) => {
    setTitleSelection(selection);
    setSelectionAnchorEl(event.currentTarget);
  };

  const handleCloseSelectionPopover = () => {
    setSelectionAnchorEl(null);
    setTitleSelection(null);
  };

  // Handler for marking selected text as manuscript number
  const handleMarkAsManuscriptNumber = async () => {
    if (!titleSelection) return;

    setIsLearningPattern(true);

    try {
      await learnFromSelection({
        title_text: title,
        selected_text: titleSelection.text,
        selection_start: titleSelection.start,
        selection_end: titleSelection.end,
        pattern_type: 'ms_number',
        library_id: typeof libraryId === 'number' ? libraryId : undefined,
      });

      // Update ms_no field with extracted value
      setMsNo(titleSelection.text);

      // Show success feedback (in a real app, use a toast notification)
      alert(t('suggestionReviewDialog.learning.success', { text: titleSelection.text }));

      handleCloseSelectionPopover();
    } catch (error) {
      console.error('Failed to learn pattern:', error);
      alert(t('suggestionReviewDialog.learning.failed'));
    } finally {
      setIsLearningPattern(false);
    }
  };

  const isLoading = createEntry.isPending || createEntryMedia.isPending;
  const hasError = createEntry.isError || createEntryMedia.isError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {mode === 'edit'
              ? t('suggestionReviewDialog.editTitle', 'Edit Suggestion')
              : t('suggestionReviewDialog.title')}
          </Typography>
          <ConfidenceBadge score={suggestion.confidence} />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && <LoadingSpinner message={t('suggestionReviewDialog.loading')} />}

        {!isLoading && (
          <>
            {hasError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('suggestionReviewDialog.error')}
              </Alert>
            )}

            {/* Pattern and Message ID */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Chip
                label={t('suggestionReviewDialog.patternChip', { pattern: translatePattern(suggestion.pattern) })}
                size="small"
                variant="outlined"
              />
              <Chip
                label={t('suggestionReviewDialog.messageChip', { messageId: suggestion.title_message_id })}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Form Fields */}
            <SelectableTextField
              label={t('suggestionReviewDialog.form.titleLabel')}
              fullWidth
              margin="normal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onTextSelect={isEditing ? handleTitleSelect : undefined}
              disabled={!isEditing}
              required
              inputProps={{ dir: 'rtl' }}
              helperText={isEditing ? t('suggestionReviewDialog.form.titleHelper') : undefined}
            />

            <TextField
              label={t('suggestionReviewDialog.form.authorLabel')}
              fullWidth
              margin="normal"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              disabled={!isEditing}
              inputProps={{ dir: 'rtl' }}
            />

            <TextField
              label={t('suggestionReviewDialog.form.msNoLabel')}
              fullWidth
              margin="normal"
              value={msNo}
              onChange={(e) => setMsNo(e.target.value)}
              disabled={!isEditing}
            />

            {/* Library Selection */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>{t('suggestionReviewDialog.form.libraryLabel')}</InputLabel>
              <Select
                value={libraryId}
                onChange={(e) => handleLibraryChange(e.target.value as string | number)}
                disabled={!isEditing}
                label={t('suggestionReviewDialog.form.libraryLabel')}
              >
                <MenuItem value="">
                  <em>{t('suggestionReviewDialog.form.noLibraryMatch')}</em>
                </MenuItem>
                {libraries.map((library) => {
                  const match = suggestion.library_matches.find(
                    (item) => item.library_id === library.id
                  );
                  return (
                    <MenuItem key={library.id} value={library.id}>
                      {library.canonical_name}
                      {match
                        ? ` (${match.match_type} • ${(match.confidence * 100).toFixed(0)}%)`
                        : ''}
                    </MenuItem>
                  );
                })}
                <Divider />
                <MenuItem value={CREATE_LIBRARY_OPTION}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" />
                    {t('suggestionReviewDialog.createLibrary.option', 'Create New Library')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {libraries.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t(
                  'suggestionReviewDialog.createLibrary.emptyHint',
                  'No libraries available yet. Create one to continue.'
                )}
              </Typography>
            )}

            {isEditing && (
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => setCreateLibraryOpen(true)}
                sx={{ mt: 0.5 }}
              >
                {t('suggestionReviewDialog.createLibrary.button', 'Create New Library')}
              </Button>
            )}

            {/* Status Selection */}
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('suggestionReviewDialog.form.statusLabel')}</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as EntryStatus)}
                disabled={!isEditing}
                label={t('suggestionReviewDialog.form.statusLabel')}
              >
                <MenuItem value={EntryStatus.DRAFT}>{t('suggestionReviewDialog.form.statuses.draft')}</MenuItem>
                <MenuItem value={EntryStatus.REVIEWED}>{t('suggestionReviewDialog.form.statuses.reviewed')}</MenuItem>
                <MenuItem value={EntryStatus.PUBLISHED}>{t('suggestionReviewDialog.form.statuses.published')}</MenuItem>
              </Select>
            </FormControl>

            {/* Notes */}
            <TextField
              label={t('suggestionReviewDialog.form.notesLabel')}
              fullWidth
              margin="normal"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isEditing}
              multiline
              rows={3}
            />

            <Divider sx={{ my: 2 }} />

            {/* Media Files Info */}
            <Typography variant="subtitle2" gutterBottom>
              {t('suggestionReviewDialog.media.title', { count: suggestion.media_ids.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('suggestionReviewDialog.media.messageIds', { ids: suggestion.media_message_ids.join(', ') })}
            </Typography>

            {/* Confidence Reasons */}
            {suggestion.reasons.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('suggestionReviewDialog.reasons.title')}
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {suggestion.reasons.map((reason, index) => (
                    <Typography component="li" key={index} variant="caption" color="text.secondary">
                      {reason}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isReadOnly ? (
          <>
            <Button onClick={onClose} startIcon={<RejectIcon />} disabled={isLoading}>
              {t('common:close', 'Close')}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {suggestion?.existing_entry_id && onViewEntry && (
              <Button
                onClick={() => onViewEntry(suggestion.existing_entry_id as number)}
                startIcon={<EditIcon />}
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {t('suggestionReviewDialog.actions.viewEntry', 'View Entry')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={handleReject} startIcon={<RejectIcon />} color="error" disabled={isLoading}>
              {t('suggestionReviewDialog.actions.reject')}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {!isEditing ? (
              <>
                <Button onClick={handleEdit} startIcon={<EditIcon />} disabled={isLoading}>
                  {t('suggestionReviewDialog.actions.edit')}
                </Button>
                <Button
                  onClick={handleAccept}
                  startIcon={<AcceptIcon />}
                  variant="contained"
                  color="success"
                  disabled={isLoading || !libraryId}
                >
                  {t('suggestionReviewDialog.actions.accept')}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setIsEditing(false)} disabled={isLoading}>
                  {t('suggestionReviewDialog.actions.cancelEdit')}
                </Button>
                <Button
                  onClick={handleAccept}
                  startIcon={<AcceptIcon />}
                  variant="contained"
                  color="success"
                  disabled={isLoading || !libraryId || !title}
                >
                  {t('suggestionReviewDialog.actions.acceptWithChanges')}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>

      {/* Text Selection Popover - for pattern learning */}
      <Popover
        open={Boolean(selectionAnchorEl)}
        anchorEl={selectionAnchorEl}
        onClose={handleCloseSelectionPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('suggestionReviewDialog.learning.popoverTitle', { text: titleSelection?.text })}
          </Typography>
          <Button
            fullWidth
            startIcon={<MarkIcon />}
            onClick={handleMarkAsManuscriptNumber}
            disabled={isLearningPattern}
            sx={{ mt: 1 }}
          >
            {isLearningPattern ? t('suggestionReviewDialog.learning.learning') : t('suggestionReviewDialog.learning.markAsMsNo')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('suggestionReviewDialog.learning.helperText')}
          </Typography>
        </Box>
      </Popover>

      <CreateLibraryDialog
        open={createLibraryOpen}
        initialName=""
        isLoading={createLibraryMutation.isPending || createAliasMutation.isPending}
        onClose={() => setCreateLibraryOpen(false)}
        onSubmit={handleCreateLibrarySubmit}
      />
    </Dialog>
  );
};

export default SuggestionReviewDialog;
